import { LoadPanel } from "devextreme-react";
import { IButtonOptions } from "devextreme-react/button";
import { FormRef } from "devextreme-react/cjs/form";
import { FileUploader, FileUploaderRef } from "devextreme-react/file-uploader";
import { ButtonItem, Form, SimpleItem } from "devextreme-react/form";
import { HtmlEditorTypes } from "devextreme-react/html-editor";
import { Popup } from "devextreme-react/popup";
import { TagBoxTypes } from "devextreme-react/tag-box";
import notify from "../../lib/notify";
import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { generateRecipients, updateAnnouncement } from "../../api/announcement";
import { useAuth } from "../../contexts/auth";
import { useCompanyContext } from "../../contexts/company";
import { RoleEnum } from "../../types";

type AnnouncementFile = {
  id: number;
  fileName: string;
  url: string;
};

type AnnouncementEditPopupProps = {
  id: number;
  title: string;
  content: string;
  roleIds: number[];
  projectIds: number[];
  files?: AnnouncementFile[]; // istniejące pliki
  onClose: (announcementUpdated: boolean) => void;
};

export default function AnnouncementEditPopup({
  id,
  title,
  content,
  roleIds,
  projectIds,
  files = [],
  onClose,
}: AnnouncementEditPopupProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { companyId } = useCompanyContext();

  const htmlFormRef = useRef<HTMLFormElement>(null);
  const devExtremeFormRef = useRef<FormRef | null>(null);
  const fileUploaderRef = useRef<FileUploaderRef | null>(null);

  const formData = useRef({
    title: "",
    content: "",
    recipients: [] as string[],
    files: [] as File[],
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [recipients, setRecipients] = useState<
    { label: string; value: string }[] | undefined
  >();

  const [existingFiles, setExistingFiles] = useState<AnnouncementFile[]>(files);
  const [filesToRemove, setFilesToRemove] = useState<number[]>([]);

  const handleRemoveFile = (fileId: number) => {
    setFilesToRemove((prev) =>
      prev.includes(fileId) ? prev : [...prev, fileId],
    );
    setExistingFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  const handleFormSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const newFiles =
        (fileUploaderRef.current?.instance().option("value") as File[]) ?? [];

      devExtremeFormRef.current?.instance().option("disabled", true);

      updateAnnouncement(id, {
        title: formData.current.title,
        content: formData.current.content,
        recipients: formData.current.recipients,
        files: newFiles,
        filesToRemove,
      })
        .then(() => onClose(true))
        .catch((error) => {
          if (error instanceof Error) {
            notify(error.message, "error");
          } else {
            notify(t("announcements.edit.messages.error"), "error");
          }
        })
        .finally(() => {
          devExtremeFormRef.current?.instance().option("disabled", false);
        });
    },
    [t, onClose, id, filesToRemove],
  );
  useEffect(() => {
    formData.current = {
      title,
      content,
      recipients: [
        ...roleIds.map((id) => `role-${id}`),
        ...projectIds.map((id) => `project-${id}`),
      ],
      files: [],
    };

    setExistingFiles(files);
    setFilesToRemove([]);

    if (!user) return;

    setLoading(true);
    generateRecipients(
      user,
      !user?.roles?.includes(RoleEnum.AdministratorGlowny) || companyId === 0
        ? undefined
        : companyId,
    )
      .then(setRecipients)
      .catch((error) => {
        setError(
          error instanceof Error
            ? error.message
            : t("common.unknownErrorOccurred"),
        );
      })
      .finally(() => setLoading(false));
  }, [t, title, content, roleIds, projectIds, files, user, companyId]);

  if (loading) {
    return <LoadPanel shading shadingColor="rgba(0, 0, 0, 0.3)" visible />;
  }

  return (
    <Popup
      visible
      title={t("announcements.edit.header")}
      onHiding={() => onClose(false)}
      maxWidth={700}
      height="auto"
      maxHeight="80vh"
      showCloseButton
      dragEnabled
    >
      {error ? (
        <div>{error}</div>
      ) : recipients === undefined ? (
        <div>{t("common.incorrectData")}</div>
      ) : (
        <form
          ref={htmlFormRef}
          encType="multipart/form-data"
          onSubmit={handleFormSubmit}
        >
          <Form ref={devExtremeFormRef} formData={formData.current}>
            <SimpleItem
              dataField="title"
              label={{ text: t("announcements.edit.inputs.title.label") }}
              editorType="dxTextBox"
              isRequired
            />
            <SimpleItem
              dataField="content"
              label={{ text: t("announcements.edit.inputs.content.label") }}
              editorType="dxHtmlEditor"
              editorOptions={
                {
                  height: 290,
                  toolbar: {
                    items: [
                      "bold",
                      "italic",
                      "underline",
                      "strike",
                      "separator",
                      "alignLeft",
                      "alignCenter",
                      "alignRight",
                      "alignJustify",
                      "separator",
                      "orderedList",
                      "bulletList",
                      "separator",
                      "link",
                      "clear",
                      "undo",
                      "redo",
                    ],
                  },
                } as HtmlEditorTypes.Properties
              }
              isRequired
            />
            <SimpleItem
              dataField="recipients"
              label={{ text: t("announcements.edit.inputs.recipients.label") }}
              editorType="dxTagBox"
              editorOptions={
                {
                  dataSource: recipients,
                  valueExpr: "value",
                  displayExpr: "label",
                  searchEnabled: true,
                } as TagBoxTypes.Properties
              }
            />
            <SimpleItem
              dataField="files"
              label={{ text: t("announcements.edit.inputs.file.jpgpngpdf") }}
            >
              <div className="flex flex-col gap-2">
                {existingFiles.map((file) => (
                  <div
                    key={file.id}
                    className="flex justify-between items-center border p-2 rounded"
                  >
                    <a
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline"
                    >
                      {file.fileName}
                    </a>
                    <button
                      type="button"
                      className="text-red-600 text-sm"
                      onClick={() => handleRemoveFile(file.id)}
                    >
                      {t("announcements.edit.inputs.file.remove")}
                    </button>
                  </div>
                ))}

                <FileUploader
                  ref={fileUploaderRef}
                  uploadMode="useForm"
                  multiple
                  accept=".jpg, .jpeg, .png, .pdf"
                  allowedFileExtensions={[".jpg", ".jpeg", ".png", ".pdf"]}
                />
              </div>
            </SimpleItem>
            <ButtonItem
              buttonOptions={
                {
                  useSubmitBehavior: true,
                  text: t("announcements.edit.inputs.submit.label"),
                  stylingMode: "contained",
                  type: "default",
                } as IButtonOptions
              }
            />
          </Form>
        </form>
      )}
    </Popup>
  );
}
